<template>
  <div class="k8-tb-graph-table">
    <div v-if="disableTitle" class="k8-tb-table-title">
      <span class="k8-tb-title">
        <span>
          {{ getTitle }}
        </span>
        <span v-if="displayTotal"> ( {{ total || 0 }} ) </span>
      </span>
      <div v-if="disableViewBtn" class="k8-tb-header-button">
        <RexButtonV3
          id="k8-btn-view-all"
          class="k8-tb-title-button-view-all"
          variant="transparent"
          :styling="{ width: 'none' }"
          @onClick="handleRoute"
        >
          <div class="k8-tb-header-button-text">
            {{ $t('viewAll') }}
          </div>
          <BaseIcon name="arrow-narrow-right" class="k8-view-all-arrow-icon" />
        </RexButtonV3>
      </div>
      <div v-if="showSearch" class="k8-tb-search">
        <RexSearch
          :placeholder="searchPlaceholder"
          @search="handleSearch"
          @clearSelection="clearSearch"
        />
      </div>
    </div>
    <RexTableCompV2
      :headers="tableConfig.headerKeys"
      :items="queryItemsApp"
      :loading="loading"
      :height="height"
      bodyVariant="secondary"
      @fetchNextPageData="
        () => {
          if (allowInfiniteScroll) {
            currentPage++;
            getTableData();
          }
        }
      "
      :enableInfiniteScroll="enableInfiniteScroll"
      :enableScrollFooter="allowInfiniteScroll"
    >
      <template
        v-for="header in tableConfig.headerKeys"
        #[`header-${header.key}`]
        :key="header.key"
      >
        <div>
          <RexTableSortField
            v-if="header.sortable"
            :name="header.name"
            @toggleSort="toggleSorting(header.key)"
            :isAsc="isAsc"
            :isActiveField="sortingField === header.key"
          />
          <div v-else>
            {{ header.name }}
          </div>
        </div>
      </template>
      <template
        v-for="(header, i) in tableConfig.headerKeys"
        #[`body-${header.key}`]="{ item }"
        :key="`${header.key}-${getRandomUniqueId()}`"
      >
        <div
          v-if="getCustomHeaders(header.key, 'icon')"
          class="k8-tb-base-icon"
        >
          <div
            :class="
              getName(item[header.key]) === icons.WARN &&
              'k8-tb-info-icon-rotate'
            "
          >
            <BaseIcon
              :name="getName(item[header.key])"
              height="1rem"
              width="1rem"
              :class="
                getName(item[header.key]) === icons.WARN && 'k8-tb-info-icon'
              "
            />
          </div>
        </div>
        <RexTooltipV3
          v-if="i === 0 && header.routable"
          :title="item[header.key]"
          class="k8-table-break-content"
        >
          <div
            class="k8-tb-ellipsis-content k8-tb-cursor-content"
            @click="onRowSelect({ option: item, key: header.key })"
          >
            {{ item[header.key] }}
          </div>
        </RexTooltipV3>
        <div
          v-else-if="getCustomHeaders(header.key, 'icon')"
          :class="[
            'k8-tb-ellipsis-content',
            getPercentClass(header.key, item[header.key]),
          ]"
        >
          {{ item[header.key] + '%' }}
        </div>
        <div
          v-else-if="header?.displayUnit"
          :class="['k8-tb-ellipsis-content']"
        >
          {{ item[header.key] + header.unit }}
        </div>
        <div
          v-else-if="header?.formatBytes"
          :class="['k8-tb-ellipsis-content']"
        >
          {{
            aggregationValue(
              item[header.key],
              header.aggregationNum,
              header.aggregation,
            ) + header.unit
          }}
        </div>
        <RexTooltipV3
          v-else-if="getTooltip(header.key)"
          :title="item[header.key]"
          class="k8-table-break-content"
        >
          <div :class="['k8-tb-ellipsis-content']">
            {{ item[header.key] }}
          </div>
        </RexTooltipV3>
        <div
          v-else
          :class="[
            'k8-tb-ellipsis-content',
            getStatusClass(header.key, item[header.key]),
          ]"
        >
          {{ item[header.key] || 'Unknown' }}
        </div>
      </template>
    </RexTableCompV2>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import RexSearch from '@/rex-components/rex-search.vue';
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import RexTableSortField from '@/rex-components/rex-TableSortField.vue';
import { SortOrder } from '@/utils/enums/sortOrder';
import {
  K8Routes,
  HeaderKeyMapping,
  K8MutationType,
  StatusType,
  CustomClassType,
  IconNames,
  headerKeyType,
  InstanceType,
  InstanceTabMapping,
  HideNamespaceList,
  WorkloadList,
  InstanceTypeList,
  InstanceStatusMapping,
  WorkloadMapping,
  TooltipKeyMapping,
} from '@/components/K8M/k8.types';
import { getRandomUniqueId, aggregationValue } from '@/utils/helper-function';
import { K8_NAVTABS } from '@/utils/templates/k8mTemplate/k8m.constants';

export default {
  name: 'GraphTableBuilder',
  components: {
    RexButtonV3,
    RexSearch,
    RexTableCompV2,
    RexTooltipV3,
    RexTableSortField,
  },
  data: function () {
    return {
      loading: true,
      queryItemsApp: [],
      sortOrder: SortOrder.DES,
      keyword: '',
      currentPage: 1,
      total: 0,
      headerMap: HeaderKeyMapping,
      icons: IconNames,
      isAsc: false,
      sortingField: this.defaultSortKey,
    };
  },
  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapGetters('k8State/k8CommonState', [
      'getCurrentK8Cluster',
      'getCurrentK8Tab',
      'getCurrentK8Namespace',
      'getCurrentK8Workload',
    ]),
    ...mapState('globalState/app', ['updateDashboard']),
    ...mapState('k8State/k8CommonState', [
      'currentDep',
      'currentStatefulset',
      'currentDaemonset',
    ]),
    disableTitle() {
      return !this.tableConfig.disableTitle;
    },
    getTitle() {
      return this.tableConfig?.displayTotal
        ? this.tableConfig?.titleWithCount
        : this.tableConfig.title;
    },
    displayTotal() {
      return this.tableConfig.displayTotal;
    },
    showSearch() {
      return this.tableConfig.showSearch;
    },
    disableViewBtn() {
      return !this.tableConfig.disableViewBtn;
    },
    searchPlaceholder() {
      return this.tableConfig.searchPlaceholder;
    },
    routeName() {
      return this.tableConfig.routeName;
    },
    allowInfiniteScroll() {
      return this.queryItemsApp?.length < this.total;
    },
  },
  props: {
    /**
     * The table configuration
     *
     */
    tableConfig: {
      type: Object,
      default: () => ({}),
    },
    scope: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '20rem',
    },
    podFromDetails: {
      type: Boolean,
      default: false,
    },
    maxPageSize: {
      type: Number,
      default: 5,
    },
    enableInfiniteScroll: {
      type: Boolean,
      default: false,
    },
    exclusiveSearchKeyword: {
      type: String,
      default: '',
    },
    defaultSortKey: {
      type: String,
      default: '',
    },
  },
  methods: {
    ...mapMutations('k8State/k8CommonState', [
      'setCurrentK8Tab',
      'setCurrentValue',
      'setK8NodeEntities',
    ]),
    ...mapActions('k8State/k8CommonState', {
      getK8TableDataAction: 'getK8TableData',
      getK8NsTableData: 'getK8NamespaceTableData',
    }),
    getRandomUniqueId,
    aggregationValue,
    setCurrentSelection(data) {
      this.setCurrentValue({
        type: this.getSelectionType(data.key),
        value: {
          key: data.option?.id,
          label: data.option[data.key],
        },
      });
    },
    onRowSelect(data) {
      if (!this.podFromDetails) {
        this.setCurrentK8Tab(data.key);
      }
      this.setCurrentSelection(data);
      this.$router.push({
        name:
          data.key === K8_NAVTABS.K8M_POD_TAB ? 'k8mPodDetails' : 'k8mDetails',
      });
    },
    handleRoute() {
      this.$router.push({
        name: K8Routes[this.routeName],
      });
      this.setCurrentK8Tab(this.tableConfig.tab);
    },
    getName(key) {
      return key >= 80
        ? IconNames.WARN
        : key <= 20
          ? IconNames.GOOD
          : IconNames.NORMAL;
    },
    getTooltip(data) {
      return TooltipKeyMapping.includes(data);
    },
    getCustomHeaders(header, type) {
      return type === 'status'
        ? this.headerMap.includes(header) && header.includes('status')
        : type === 'text'
          ? this.headerMap.includes(header) &&
            (header.includes('pods_available') ||
              header.includes('pods_unavailable'))
          : this.headerMap.includes(header) &&
            !header.includes('status') &&
            !header.includes('pods_available') &&
            !header.includes('pods_unavailable');
    },
    isWorkload() {
      return WorkloadList.findIndex(
        (item) => item.key === this.getCurrentK8Tab,
      ) === -1
        ? false
        : true;
    },
    getCurrWorkloadData() {
      return this[
        K8MutationType[
          this.isWorkload()
            ? this.getCurrentK8Tab
            : this.getCurrentK8Workload.key
        ]
      ]?.label;
    },
    checkCurrWorkload() {
      return (
        this[
          K8MutationType[
            this.isWorkload()
              ? this.getCurrentK8Tab
              : this.getCurrentK8Workload.key
          ]
        ]?.key !== 'all'
      );
    },
    getNamespaceInstanceProperty() {
      if (
        !HideNamespaceList.includes(this.getCurrentK8Tab) &&
        this.getCurrentK8Namespace.key !== 'all'
      ) {
        return this.getCurrentK8Namespace.label;
      }
    },
    getType() {
      return WorkloadList[
        WorkloadList.findIndex((item) => item.key === this.getCurrentK8Tab)
      ].label;
    },
    getWorkloadInstanceProp() {
      const propList = [];
      if (
        this.getCurrentK8Workload.key === K8_NAVTABS.K8M_DEP_TAB ||
        this.getCurrentK8Workload.key === K8_NAVTABS.K8M_STAT_TAB ||
        this.getCurrentK8Workload.key === K8_NAVTABS.K8M_DAEMON_TAB ||
        this.getCurrentK8Workload.key === 'NONE'
      ) {
        propList.push({
          key: 'prt',
          value: this.isWorkload()
            ? this.getType()
            : this.getCurrentK8Workload.key === 'NONE'
              ? 'NONE'
              : this.getCurrentK8Workload.label,
        });
        if (this.checkCurrWorkload()) {
          propList.push({
            key: 'prn',
            value: this.getCurrWorkloadData(),
          });
        }
      }
      return propList;
    },
    getInstanceProps() {
      const propList = [];
      propList.push({
        key: 'prt',
        value: WorkloadMapping[this.getCurrentK8Tab],
      });
      propList.push({
        key: 'prn',
        value: this[K8MutationType[this.getCurrentK8Tab]].label,
      });
      return propList;
    },
    getRequestParams() {
      const reqParams = {};
      const metricList = [];
      const labeledMetricsList = [];
      const metaPropertyList = [];
      const instanceType = this.tableConfig?.instanceType;
      this.tableConfig.headerKeys.map((item) => {
        if (item.type) {
          if (item.type === headerKeyType.METRIC) {
            metricList.push(item.key);
          } else if (item.type === headerKeyType.LABELED_METRICS) {
            metricList.push(item.key);
            labeledMetricsList.push(item.key);
          } else {
            metaPropertyList.push(item.key);
          }
        }
      });
      if (this.getCurrentK8Tab === K8_NAVTABS.K8M_POD_TAB) {
        reqParams['namespace'] = this.getNamespaceInstanceProperty();
        if (this.getWorkloadInstanceProp()?.[0]) {
          reqParams['parentResourceType'] =
            this.getWorkloadInstanceProp()[0].value;
        }
        if (this.getWorkloadInstanceProp()?.[1]) {
          reqParams['parentResourceName'] =
            this.getWorkloadInstanceProp()[1].value;
        }
      }
      if (this.isWorkload() && !this.$route.path.includes('details')) {
        reqParams['namespace'] = this.getNamespaceInstanceProperty();
      }
      if (this.isWorkload() && this.$route.path.includes('details')) {
        if (this.getInstanceProps().length > 0) {
          reqParams['parentResourceType'] = this.getInstanceProps()[0].value;
          reqParams['parentResourceName'] = this.getInstanceProps()[1].value;
        }
      }
      reqParams['serviceId'] = this.getCurrentK8Cluster?.key;
      reqParams['metaPropertyList'] = metaPropertyList;
      reqParams['metricsList'] = metricList;
      reqParams['labeledMetricsList'] = labeledMetricsList;
      reqParams['keyword'] = this.keyword;
      reqParams['instanceType'] = instanceType;

      return reqParams;
    },
    /**
     * Constructs the required params and fetches the data during the action.
     */
    async getTableData() {
      const pagination = {
        pageNum: this.currentPage,
        pageSize: this.maxPageSize,
        needTotal: true,
      };
      const sortCriteria = [
        {
          field: this.sortingField,
          order: this.isAsc ? SortOrder.ASC : SortOrder.DES,
        },
      ];
      if (this.tableConfig.instanceType !== InstanceType['namespaces']) {
        const params = {
          tableOverviewRequest: this.getRequestParams(),
          duration: this.getDurationTime,
          pagination: pagination,
          sortCriteria: sortCriteria,
        };
        try {
          const metricData = await this.getK8TableDataAction(params);
          if (
            this.tableConfig?.instanceType ===
              InstanceType[K8_NAVTABS.K8M_NODE_TAB] &&
            this.$route.name === 'k8mClusterOverview'
          ) {
            const entities = this.getEntityIds(metricData);
            this.setK8NodeEntities(entities);
          }
          if (
            this.tableConfig?.instanceType ===
              InstanceType[K8_NAVTABS.K8M_NODE_TAB] &&
            this.$route.name === K8Routes.nodes
          ) {
            this.$emit('getNodesTotal', metricData[0]?.totalEntities);
          }
          this.total = metricData[0]?.totalEntities;
          this.queryItemsApp.push(...this.formatTableResp(metricData));
          this.loading = false;
        } catch (err) {
          console.log(err);
          this.loading = false;
        }
      } else {
        const params = {
          tableOverviewRequest: {
            serviceId: this.getCurrentK8Cluster.key,
            metricsList: [
              'k8s_namespace_v2_namespace_status',
              'k8s_namespace_v2_service_total',
            ],
            labeledMetricsList: ['k8s_namespace_v2_namespace_status'],
            keyword: this.keyword,
            instanceType: 'K8S_NAMESPACE',
            metaPropertyList: ['metaProperty1'],
          },
          duration: this.getDurationTime,
          pagination: pagination,
          instanceList: [
            'K8S_POD',
            'K8S_DAEMON_SET',
            'K8S_DEPLOYMENT',
            'K8S_STATEFULSET',
          ],
          sortCriteria: sortCriteria,
        };
        try {
          const metricData = await this.getK8NsTableData(params);
          this.queryItemsApp.push(...this.formatTableResp(metricData));
          this.total = metricData[0]?.totalEntities;
          this.loading = false;
        } catch (err) {
          console.log(err);
          this.loading = false;
        }
      }
    },
    handleSearch(data) {
      this.keyword = data.text;
      this.resetAndGetTableData();
    },
    clearSearch() {
      this.keyword = '';
      this.resetAndGetTableData();
    },
    getPercentClass(header, key) {
      if (this.getCustomHeaders(header, '')) {
        return parseFloat(key) <= 20
          ? CustomClassType.GOOD
          : parseFloat(key) >= 80
            ? CustomClassType.POOR
            : CustomClassType.TANGERINE;
      }
      return '';
    },
    getStatusClass(header, key) {
      if (this.getCustomHeaders(header, 'status')) {
        if (key && key !== 'Unknown') {
          return StatusType.ACTIVE_LIST.includes(key)
            ? 'good-chip'
            : StatusType.INACTIVE_LIST.includes(key)
              ? 'poor-chip'
              : 'tangerine-chip';
        } else {
          return 'unknown-chip';
        }
      } else if (this.getCustomHeaders(header, 'text')) {
        if (header.includes('pods_available')) {
          return parseFloat(key) === 0
            ? CustomClassType.POOR
            : CustomClassType.GOOD;
        } else if (header.includes('pods_unavailable')) {
          return parseFloat(key) === 0
            ? CustomClassType.GOOD
            : CustomClassType.POOR;
        }
      }
      return '';
    },
    getSelectionType(key) {
      return K8MutationType[key];
    },
    formatTableResp(list) {
      const metricResponse = [];
      list.map((item) => {
        const mergedObject = item.metricsValue.reduce((result, input) => {
          if (input) {
            if (
              this.tableConfig.instanceType !== InstanceType['namespaces'] &&
              Object.values(InstanceType).includes(input.name)
            ) {
              result[InstanceTabMapping[input.name]] = input.value.includes(
                ':::',
              )
                ? input.value.split(':::')[input.value.split(':::').length - 1]
                : input.value;
            } else if (
              this.tableConfig.instanceType === InstanceType['namespaces']
            ) {
              result[input.name] = input.value.includes(':::')
                ? input.value.split(':::')[input.value.split(':::').length - 1]
                : input.value;
            } else {
              result[input.name] = input.value;
            }
            if (InstanceTypeList.includes(this.tableConfig.instanceType)) {
              if (input.name.includes('status')) {
                result[input.name] =
                  InstanceStatusMapping[this.tableConfig.instanceType][
                    input.value.split(',')[0]
                  ];
              }
            }
          }
          result['_id'] = `${getRandomUniqueId()}`;
          return result;
        }, {});
        metricResponse.push(mergedObject);
      });
      return metricResponse;
    },
    getEntityIds(list) {
      return list.map((item) => {
        return {
          key: item.metricsValue.find((o) => o.name === 'id').value,
          label: item.metricsValue.find(
            (o) => o.name === this.tableConfig.instanceType,
          ).value,
        };
      });
    },
    resetTableData() {
      this.queryItemsApp = [];
      this.loading = true;
      this.total = 0;
      this.currentPage = 1;
    },
    resetAndGetTableData() {
      this.resetTableData();
      this.getTableData();
    },
    toggleSorting(sortingField) {
      this.isAsc = !this.isAsc;
      this.sortingField = sortingField;
      this.resetAndGetTableData();
    },
  },
  watch: {
    getDurationTime() {
      this.resetAndGetTableData();
    },
    'getCurrentK8Namespace.key'() {
      this.resetAndGetTableData();
    },
    'getCurrentK8Cluster.key'() {
      this.resetAndGetTableData();
    },
    'getCurrentK8Workload.key'() {
      this.resetAndGetTableData();
    },
    'currentDep.key'() {
      this.resetAndGetTableData();
    },
    'currentStatefulset.key'() {
      this.resetAndGetTableData();
    },
    'currentDaemonset.key'() {
      this.resetAndGetTableData();
    },
    exclusiveSearchKeyword(newVal) {
      if (newVal) {
        this.handleSearch(newVal);
      } else {
        this.clearSearch();
      }
    },
  },
  mounted() {
    this.getTableData();
  },
};
</script>
<style lang="scss" scoped>
.k8-tb-graph-table {
  margin-right: 1rem;
  :deep(.rex-service-sel) {
    background: $bg-secondary !important;
  }
  :deep(.items) {
    background: $bg-secondary !important;
  }
}
:deep(.header-container) {
  font-size: 1rem;
}
.k8-tb-table-title {
  @include flex-center-justify(space-between);
  height: 3rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  color: $font-primary;
}
.k8-tb-header-button > button {
  height: 1.43rem;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: flex-end;
  &:hover {
    text-decoration: underline;
  }
}
.k8-tb-title-button-view-all {
  @include dropshadow-effect(0);
  border: none;
}

.k8-tb-title {
  margin-right: 1rem;
  font-size: 1.25rem;
  font-family: $semi-bold;
}

.k8-tb-header-button-text {
  font-family: RakutenSansUI-Regular !important;
  color: $color-brand-accent;
  font-size: 1rem;
  margin-right: 0.3rem;
}
.k8-tb-search {
  @include flex-column;
  align-items: flex-end;
  margin-top: 1.0625rem;
  margin-bottom: 1.25rem;
}
.k8-tb-ellipsis-content {
  @include truncate(100%);
}
.tangerine {
  color: $color-brand-accent;
  padding: 0.4rem 1rem;
}
.poor {
  color: $poor;
  padding: 0.4rem 1rem;
}
.good {
  color: $good;
  padding: 0.4rem 1rem;
}
.k8-tb-cursor-content {
  border-bottom: 0.01rem solid $color-white;
  cursor: pointer;
}
.k8-tb-cursor-content:hover {
  color: $color-brand-accent;
  border-bottom: 0.01rem solid $color-brand-accent;
}
.k8-view-all-arrow-icon {
  padding-bottom: 0.3rem;
  @include svg-fill($color-brand-accent);
}
.k8-tb-info-icon {
  @include svg-fill($color-dark-red);
}
.k8-tb-info-icon-rotate {
  transform: rotate(180deg);
  padding-bottom: 0.4rem;
}
.k8-tb-base-icon {
  padding-right: 0.4rem;
}
.unknown-chip {
  border: 0.1rem solid $font-light-gray;
  border-radius: 50rem;
  padding: 0.2rem 1rem;
  color: $font-light-gray;
}
.good-chip {
  border: 0.1rem solid $good;
  border-radius: 50rem;
  padding: 0.2rem 1rem;
  color: $good;
}
.poor-chip {
  border: 0.1rem solid $poor;
  border-radius: 50rem;
  padding: 0.2rem 1rem;
  color: $poor;
}
.tangerine-chip {
  border: 0.1rem solid $color-brand-accent;
  border-radius: 50rem;
  padding: 0.2rem 1rem;
  color: $color-brand-accent;
}
.k8-table-break-content {
  overflow: hidden;
  white-space: nowrap;
}
</style>
